import { LocalDictionaryFactory } from '../../api/api.module'
import { ColumnModel } from '../../api/api.models'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { LoggerService } from '../../common/common.module'
import { GridOptions, GridTypes, GridController, ButtonsOptions } from '../../common/grid/grid.controller'

import { IUserSession, OperatorLayoutSettings, EventsNames, LocalizationHelper, TemplateHelper, WhereClauseHelper, ConfigHelper } from '../../../helpers'

import { OpLocation, OpLocationType, OpViewColumn } from '../../../models/core'
import { Enums, Activity } from '../../../models/common'
import { ColumnInfo } from '../../../models/custom'

import { GroupingLocationTypeHelper } from '../grouping-locations-types.helper'
import { SystemService } from '../../../services/system'

interface GroupingLocationTypeDetailsScope extends DetailsScope {
    GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME: string
    GROUPING_LOCATION_TYPE_COMMON_TAB_NAME: string
    GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME: string

    id: number
    groupingLocationsAssignedTypesGridOptions: GridOptions
    details: any
    assignedLocationsCount: number
    noDefinedContent: string
}

export class GroupingLocationsTypeDetailDirectiveController extends Details<GroupingLocationTypeDetailsScope> {

    // Consts

    private get GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME() { return 'GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME' }
    private get GROUPING_LOCATION_TYPE_COMMON_TAB_NAME() { return 'GROUPING_LOCATION_TYPE_COMMON_TAB_NAME' }
    private get GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME() { return 'GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME' }

    // Consts End


    // Members

    private groupingLocationTypeAssignedGrid: GridController<OpLocation>
    private locationType: OpLocationType

    // Members End


    constructor(
        $scope: GroupingLocationTypeDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        private systemService: SystemService,
        userSession: IUserSession,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.LocationPopupTabActive, userSession, referenceType)

        this.setEvents()
        this.setOptions()

        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.GROUPING_LOCATION_TYPE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }


    // Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options
        this.onClear()
        this.showPopup()
        this.onRefresh(options.referenceObject)
    }

    private onRefresh(toRefresh: OpLocationType = null): void {
        this.$scope.options.isDataLoaded = false
        this.locationType = toRefresh

        this.$scope.popupTitle = toRefresh.name
        this.$scope.showTabs = true

        this.$scope.details = GroupingLocationTypeHelper.getGroupingLocationTypeDetailsFields(toRefresh)

        $.when(
            this.setAssignedLocationsTypeGridOptions()
        )
            .always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false
        this.$scope.groupingLocationsAssignedTypesGridOptions = null
        this.locationType = null
    }

    private onClose(): void {
        this.$scope.groupingLocationsAssignedTypesGridOptions = null
        this.hidePopup()
    }

    private onParamsEdit(): void {
        const createEditInitParams: CreateEditInitParams = {
            moduleName: this.moduleName,
            idReferenceType: this.referenceType,
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            referenceObjectId: this.locationType.idLocationType,
            referenceObject: GroupingLocationTypeHelper.createOpObject(this.locationType),
            sections: null,
            useBusinessObjects: true,

            customDataTypes: {
                paramsRequired: [Enums.DataType.GROUPING_LOCATION_TYPE_IS_ENABLED, Enums.DataType.HELPER_DESCRIPTION],
                paramsToEdit: [Enums.DataType.GROUPING_LOCATION_TYPE_IS_ENABLED, Enums.DataType.HELPER_DESCRIPTION]
            }
        }

        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    // Events End


    // Setup 

    getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.GROUPING_LOCATION_TYPE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.GroupingLocationTypeParameters)) {
            items.push({
                name: this.GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME,
                showInTab: this.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.GROUPING_LOCATION_TYPE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.GROUPING_LOCATION_TYPE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.GroupingLocationTypeParameters),

                editMethod: () => this.onParamsEdit(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.LocationAccordionsSettings, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.GROUPING_LOCATION_TYPE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.GroupingLocationTypeAssignedLocations)) {
            items = []
            items.push({
                name: this.GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME,
                showInTab: this.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedLocations),

                visible: true,

                canEdit: false,
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.LocationAccordionsSettings, 1, items))
        }

        return accordions
    }

    getTabs(): any[] {
        return [{
            template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),

            name: this.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME
        }]
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.accordionsNameToShow = this.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.$scope.noDefinedContent = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDefinedContent)
        this.$scope.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME = this.GROUPING_LOCATION_TYPE_COMMON_TAB_NAME
        this.$scope.GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME = this.GROUPING_LOCATION_TYPE_PARAMS_ACCORDION_NAME
        this.$scope.GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME = this.GROUPING_LOCATION_TYPE_ASSIGNED_LOCATIONS_ACCORDION_NAME
    }

    private setAssignedLocationsTypeGridOptions(): JQueryPromise<void> {

        return this.userSession.getDataProvider().System
            .getRequestColumnInfo(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS_TO_DISPLAY, Enums.SimaxModuleName.Location)
            .then((columns: ColumnModel[]) => {
                this.$scope.groupingLocationsAssignedTypesGridOptions = {
                    addCustomFilters: true,
                    idReferenceType: this.referenceType,
                    columnsDictionary: columns,
                    name: 'GroupingLocationTypeAssignedLocationsGrid',
                    type: GridTypes.PAGINATED_SMALL,
                    buttonsOptions: this.getButtonsOptions(),
                    getDataSourceMethod: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
                        return this.getDataSourceMethod(columns, pageNbr, pageSize, customWhereClause)
                    },
                    onInitialized: (e: any, grid: GridController<OpLocation>) => {
                        this.setGroupingLocationTypeAssignedGrid(grid)
                    },
                    onRowDoubleClick: (row: any) => {
                        this.onRowDoubleClick()
                    },
                    contextMenuItems: (isTouchScreen: boolean = false) => this.getAssignedLocationsContextMenuItems(isTouchScreen),
                }
            })
    }

    // Setup end


    // Private Methods 

    private getDataSourceMethod(columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<any> {
        return GroupingLocationTypeHelper
            .getLocationTypeViewColumn(this.userSession)
            .then((opViewColumn: OpViewColumn) => {
                customWhereClause = WhereClauseHelper.appendEquals(customWhereClause, opViewColumn.name, this.locationType.idLocationType)

                return this.userSession.getDataProvider().GroupingLocation.getGroupingLocationFilter(columns, pageNbr, pageSize, customWhereClause)
            })
    }

    private onRowDoubleClick(): void {
        if (this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_LIST)) {
            this.systemService.openDetails({
                moduleName: Enums.SimaxModuleName.Location,
                referenceObjectId: this.groupingLocationTypeAssignedGrid.selectedItemId
            })
        }
    }

    private setGroupingLocationTypeAssignedGrid(grid: GridController<OpLocation>): void {
        if (grid != null) {
            this.groupingLocationTypeAssignedGrid = grid
        }
    }

    private getButtonsOptions(): ButtonsOptions {
        const buttonsOptions: ButtonsOptions = {
            enabled: true,
        }
        return buttonsOptions
    }

    private getAssignedLocationsContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items: any = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_LIST)) {
            items.push({
                beginGroup: false,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ShowLocationDetails), ConfigHelper.Icon.DETAILS, this.userSession),

                onItemClick: (e: any) => {
                    if (this.groupingLocationTypeAssignedGrid.selectedItemId != null) {
                        this.systemService.openDetails({
                            moduleName: Enums.SimaxModuleName.Location,
                            referenceObjectId: this.groupingLocationTypeAssignedGrid.selectedItemId
                        })
                    }
                }
            })
        }
        return items
    }

    // Private Methods end


    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}